/* eslint-disable no-console */
import React from 'react';
import _uniqBy from 'lodash/uniqBy';
import { guardrail, useInterval } from '../hooks';

const RESET_DATA_THROUGH_HTTP_EVERY_MS = 60000; // 24 * 60 * 60 * 1000;
const FALLBACK_TO_HTTP_INTERVAL_MS = 2000;
const ALLOWED_HTTP_ERROR_COUNT = 3;

const STRATEGY = {
  WEBSOCKET: 'WEBSOCKET',
  HTTP_POLLING: 'HTTP_POLLING'
};

/**
 * This hooks setups initial data fetching from HTTP endpoint and then proceeds listening to updates from websocket.
 * It also ensures the integrity of data in front-end by ordering and uniquifying the data if needed.
 * @param {string | (rawData => identifier)} rawDataUniqBy identifies by what rawData should be filtered,
 * in case there would be any duplicates incoming from websocket. Can be a string or identity function
 */
export function useWSHook({
  test,
  processMessageConditions,
  fetchInitDataToRawData,
  processRawData,
  wsHook,
  processWSEventDataToRawData,
  initialProcessedDataState = [],
  rawDataUniqBy,
  vetoCondition
}) {
  const [error, setError] = React.useState(false);
  const [processedData, setProcessedData] = React.useState(initialProcessedDataState);
  const [loading, setLoading] = React.useState(true);
  const [activeStrategy, setActiveStrategy] = React.useState(STRATEGY.WEBSOCKET);

  const didCancelRef = React.useRef(false);
  const queueWSEventsRef = React.useRef(true);
  const rawDataRef = React.useRef([]);
  const outOfOrderEventsRef = React.useRef([]);
  const queuedEventsRef = React.useRef([]);
  const httpPollsErrorCountRef = React.useRef(0);

  const doFetchInitData = async () => {
    queueWSEventsRef.current = true;

    let newProcessedData;
    let initRawData;

    try {
      initRawData = await fetchInitDataToRawData();
      httpPollsErrorCountRef.current = 0; // reset errors count, after successful fetch.
      newProcessedData = processRawData(initRawData);
    } catch (e) {
      console.error(`Error while fetching initial data:`);
      console.error(e);
      httpPollsErrorCountRef.current += 1;
      guardrail(() => {
        if (httpPollsErrorCountRef.current >= ALLOWED_HTTP_ERROR_COUNT) {
          // we only want to show that something is wrong to user, after 3 tries, instead of the first one.
          setError(true);
        }
        setLoading(false);
        queueWSEventsRef.current = false;
      }, didCancelRef.current || vetoCondition);
      return;
    }

    guardrail(() => {
      rawDataRef.current = initRawData;
      setProcessedData(newProcessedData);
      queueWSEventsRef.current = false;
      setLoading(false);
    }, didCancelRef.current || vetoCondition);
  };

  const refetch = ({ dontReopenWs = false } = {}) => {
    didCancelRef.current = true;
    queueWSEventsRef.current = true;
    outOfOrderEventsRef.current = [];
    rawDataRef.current = [];

    setProcessedData(initialProcessedDataState);
    setError(false);
    setLoading(true);

    if (!dontReopenWs) wsHook.reopenWs();
  };

  const onWsMessage = React.useCallback(
    event => {
      try {
        if (wsHook.error) return;
        // Discard events if they come from a different URL than the one we are currently listening to.
        if (event?.currentTarget.url !== wsHook?.ws.url) return;

        const eventData = JSON.parse(event.data);
        if (processMessageConditions && !processMessageConditions(eventData)) return;

        let newRawData = [...rawDataRef.current];
        let newOutOfOrderEvents = [...outOfOrderEventsRef.current];

        if (queueWSEventsRef.current) {
          // queue websockets events while we are waiting for http request response.
          guardrail(() => {
            queuedEventsRef.current = [...queuedEventsRef.current, eventData];
          }, didCancelRef.current || vetoCondition);
          return;
        }

        if (!queueWSEventsRef.current && queuedEventsRef.current.length > 0) {
          queuedEventsRef.current.forEach(queuedEventData => {
            ({ newRawData, newOutOfOrderEvents } = processWSEventDataToRawData(
              queuedEventData,
              newRawData,
              newOutOfOrderEvents
            ));
          });

          guardrail(() => {
            queuedEventsRef.current = [];
          }, didCancelRef.current || vetoCondition);
        }

        ({ newRawData, newOutOfOrderEvents } = processWSEventDataToRawData(
          eventData,
          newRawData,
          newOutOfOrderEvents
        ));

        // uniquify and order the data because there are no unique/order guarantees with web socket.
        if (rawDataUniqBy) newRawData = _uniqBy(newRawData, rawDataUniqBy);

        const newProcessedData = processRawData(newRawData);

        if (
          outOfOrderEventsRef.current &&
          Array.isArray(outOfOrderEventsRef.current) &&
          outOfOrderEventsRef.current.length > 1000
        ) {
          // take last 1000 entries;
          // this assumes all handlers will be pushing and not unshifting;
          outOfOrderEventsRef.current = outOfOrderEventsRef.current.slice(-1000);
        }

        guardrail(() => {
          rawDataRef.current = newRawData;
          outOfOrderEventsRef.current = newOutOfOrderEvents;
          setProcessedData(newProcessedData);
        }, didCancelRef.current || vetoCondition);
      } catch (e) {
        setError(true);
        console.error(`Error happened while processing ws message: `, e);
      }
    },
    [
      wsHook.ws,
      wsHook.error,
      processMessageConditions,
      processRawData,
      processWSEventDataToRawData,
      rawDataUniqBy,
      vetoCondition
    ]
  );

  if (test) {
    window.onWsMessage = onWsMessage;
    window.wsHook = wsHook;
  }

  /** Fetch init data */
  React.useEffect(
    () => {
      if (!wsHook.ws) return;
      doFetchInitData();
    },
    [wsHook.ws] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** Setup data reset through HTTP endpoint */
  useInterval(
    () => {
      doFetchInitData();
    },
    activeStrategy === STRATEGY.WEBSOCKET ? RESET_DATA_THROUGH_HTTP_EVERY_MS : FALLBACK_TO_HTTP_INTERVAL_MS
  );

  /** Setup listening to further data updates. */
  React.useEffect(() => {
    if (!wsHook.ws) return undefined;

    const onError = () => event => {
      console.error(`We received an error from websocket:`);
      console.error(event);
      setError(true);
    };

    didCancelRef.current = false; // reset didCancel just before WS event handlers set, because this is start of data flow.
    wsHook.ws.addEventListener('message', onWsMessage);
    wsHook.ws.addEventListener('error', onError);

    return () => {
      if (!wsHook.ws) return;
      wsHook.ws.removeEventListener('message', onWsMessage);
      wsHook.ws.removeEventListener('error', onError);
    };
  }, [wsHook.ws, onWsMessage]);

  // /** Setup fallback to http requests if we didn't manage to connect to WS for some time */
  React.useEffect(
    () => {
      if (!wsHook.inited) return undefined;

      console.log('detected WS error change');
      let switchToHttpTimeout;

      const switchToHttpPolling = () => {
        console.log('switch to http polling');
        // give a 1 second gap before switching to HTTP polling
        switchToHttpTimeout = setTimeout(() => {
          console.log('switched to http polling');
          setActiveStrategy(STRATEGY.HTTP_POLLING);
        }, 1000);
      };

      const switchToSocketStrategy = () => {
        console.log('switch to socket strategy');
        setActiveStrategy(STRATEGY.WEBSOCKET);
      };

      if (wsHook.error) {
        switchToHttpPolling();
      } else {
        switchToSocketStrategy();
      }

      return () => {
        clearTimeout(switchToHttpTimeout);
      };
    },
    [wsHook.error] //eslint-disable-line
  );

  React.useEffect(
    () => {
      if (!wsHook.inited) return undefined;
      if (wsHook.error) return undefined;

      return () => {
        // detect open and close (state re-set) of new WS, therefore do perform re-fetch on all hooks using that WebSocket
        // except don't re-open the WS again, otherwise this would be infinite loop.
        refetch({ dontReopenWs: true });
      };
    },
    [wsHook.ws] // eslint-disable-line
  );

  return {
    refetch,
    error,
    loading,
    data: processedData
  };
}
